import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TEMPLATES = [
  { value: 'ieee', label: 'IEEE Conference' },
  { value: 'springer', label: 'Springer LNCS' },
];

function ConvertPage({ user }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [template, setTemplate] = useState('ieee');
  const [imageFormat, setImageFormat] = useState('png');
  const [status, setStatus] = useState('idle'); // idle | converting | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(true);
  const [limitHit, setLimitHit] = useState(false);

  useEffect(() => {
    if (user) {
      const toastKey = 'word2latex_new_feature_v1';
      if (!localStorage.getItem(toastKey)) {
        localStorage.setItem(toastKey, '1');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 7000);
      }
    }
  }, [user]);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setErrorMsg('Only .docx files are supported. Please convert your file to .docx format first.');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setErrorMsg('File too large. Maximum size is 5MB for best conversion results.');
      return;
    }
    setErrorMsg('');
    setFile(f);
    setStatus('idle');
    setResult(null);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, []);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const handleConvert = async () => {
    if (!file) { setErrorMsg('Please select a .docx file first.'); return; }

    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    setStatus('converting');
    setProgress(10);
    setErrorMsg('');

    const tick = setInterval(() => {
      setProgress(p => Math.min(p + 8, 85));
    }, 1200);

    try {
      const formData = new FormData();
      formData.append('docx', file);
      formData.append('template', template);
      formData.append('imageFormat', imageFormat);

      const res = await fetch(`${API_BASE}/api/convert/docx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      clearInterval(tick);
      setProgress(100);

      const data = await res.json();
      if (res.status === 429) {
        setLimitHit(true);
        setStatus('error');
        return;
      }
      if (!res.ok || !data.success) throw new Error(data.error || 'Conversion failed');

      setResult(data);
      setStatus('done');
    } catch (err) {
      clearInterval(tick);
      setStatus('error');
      setErrorMsg(err.message || 'Conversion failed. Please try again.');
    }
  };

  const openInEditor = () => {
    if (result) navigate('/editor', { state: { openProjectId: result.projectId } });
  };

  const reset = () => {
    setFile(null);
    setStatus('idle');
    setResult(null);
    setErrorMsg('');
    setProgress(0);
  };

  return (
    <div style={styles.page}>
      {showWelcomePopup && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupCard}>
            <div style={styles.popupIcon}>🚀</div>
            <h2 style={styles.popupTitle}>Word → LaTeX Converter</h2>
            <p style={styles.popupBody}>
              Tired of writing LaTeX code from scratch? Upload your Word document and get ready-to-compile LaTeX in seconds! Currently supporting <strong>IEEE Conference</strong>, <strong>Springer LNCS</strong> templates.
            </p>
            <button style={styles.popupBtn} onClick={() => setShowWelcomePopup(false)}>
              Get Started
            </button>
          </div>
        </div>
      )}
      {showToast && (
        <div style={styles.toast}>
          <span style={styles.toastIcon}>✨</span>
          <div>
            <div style={styles.toastTitle}>New! Word → LaTeX</div>
            <div style={styles.toastBody}>Upload your Word document and get LaTeX code instantly. Try it below!</div>
          </div>
          <button style={styles.toastClose} onClick={() => setShowToast(false)}>×</button>
        </div>
      )}
      <div style={styles.card}>
        <h1 style={styles.title}>Convert Word to LaTeX</h1>
        <p style={styles.subtitle}>
          Upload a .docx file — pandoc converts it, then Gemini AI cleans it up for your chosen template.
        </p>

        {!user && (
          <div style={styles.authGate}>
            <div style={styles.authGateIcon}>🔒</div>
            <p style={styles.authGateMsg}>Please login or register to use Word → LaTeX converter</p>
            <div style={styles.authGateBtns}>
              <button style={{ ...styles.primaryBtn, flex: 'unset', width: '140px' }} onClick={() => navigate('/login')}>Login</button>
              <button style={styles.signupBtn} onClick={() => navigate('/register')}>Sign Up Free</button>
            </div>
          </div>
        )}

        {user && limitHit && (
          <div style={styles.limitBanner}>
            <div style={styles.limitIcon}>⚠️</div>
            <div>
              <div style={styles.limitTitle}>Free conversion used</div>
              <div style={styles.limitMsg}>Upgrade to Pro for unlimited conversions.</div>
            </div>
            <button style={styles.upgradeBtn} onClick={() => navigate('/pricing')}>Upgrade to Pro</button>
          </div>
        )}

        {user && !limitHit && (
          <>
            {/* Drop zone */}
            <div
              style={{ ...styles.dropzone, ...(dragging ? styles.dropzoneDrag : {}), ...(file ? styles.dropzoneFilled : {}) }}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
              {file ? (
                <div style={styles.fileInfo}>
                  <span style={styles.fileIcon}>📄</span>
                  <div>
                    <div style={styles.fileName}>{file.name}</div>
                    <div style={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    style={styles.removeBtn}
                    onClick={e => { e.stopPropagation(); reset(); }}
                  >×</button>
                </div>
              ) : (
                <div style={styles.dropPrompt}>
                  <div style={styles.dropIcon}>📁</div>
                  <div style={styles.dropText}>Drag & drop your .docx file here</div>
                  <div style={styles.dropOr}>or click to browse</div>
                </div>
              )}
            </div>

            {/* File validation error */}
            {errorMsg && status === 'idle' && <div style={styles.error}>{errorMsg}</div>}

            {/* Options row */}
            <div style={styles.optionsRow}>
              <div style={styles.optionGroup}>
                <label style={styles.label}>Template</label>
                <select
                  value={template}
                  onChange={e => setTemplate(e.target.value)}
                  style={styles.select}
                  disabled={status === 'converting'}
                >
                  {TEMPLATES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div style={styles.optionGroup}>
                <label style={styles.label}>Image Format</label>
                <div style={styles.toggle}>
                  {['png', 'eps'].map(fmt => (
                    <button
                      key={fmt}
                      style={{ ...styles.toggleBtn, ...(imageFormat === fmt ? styles.toggleActive : {}) }}
                      onClick={() => setImageFormat(fmt)}
                      disabled={status === 'converting'}
                    >
                      {fmt.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            {status === 'converting' && (
              <div style={styles.progressWrap}>
                <div style={styles.progressTrack}>
                  <div style={{ ...styles.progressBar, width: progress + '%' }} />
                </div>
                <div style={styles.progressLabel}>
                  {progress < 30 ? 'Uploading...' : progress < 70 ? 'Running pandoc...' : 'Cleaning up with Gemini AI...'}
                </div>
              </div>
            )}

            {/* Conversion error */}
            {errorMsg && status === 'error' && <div style={styles.error}>{errorMsg}</div>}

            {/* Success */}
            {status === 'done' && result && (
              <div style={styles.success}>
                <div style={styles.successIcon}>✅</div>
                <div>
                  <div style={styles.successTitle}>Conversion complete!</div>
                  <div style={styles.successDetail}>
                    Project "<strong>{result.projectName}</strong>" created
                    {result.imageCount > 0 && ` · ${result.imageCount} image${result.imageCount > 1 ? 's' : ''} extracted`}
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div style={styles.actions}>
              {status === 'done' ? (
                <>
                  <button style={styles.primaryBtn} onClick={openInEditor}>Open in Editor</button>
                  <button style={styles.secondaryBtn} onClick={reset}>Convert Another</button>
                </>
              ) : (
                <button
                  style={{ ...styles.primaryBtn, ...(status === 'converting' || !file ? styles.disabledBtn : {}) }}
                  onClick={handleConvert}
                  disabled={status === 'converting' || !file}
                >
                  {status === 'converting' ? 'Converting...' : 'Convert to LaTeX'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #eff6ff 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px'
  },
  card: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 8px'
  },
  subtitle: {
    color: '#64748b',
    fontSize: '0.95rem',
    margin: '0 0 28px',
    lineHeight: 1.5
  },
  dropzone: {
    border: '2px dashed #cbd5e1',
    borderRadius: '12px',
    padding: '32px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '24px',
    background: '#f8fafc'
  },
  dropzoneDrag: {
    borderColor: '#10b981',
    background: '#f0fdf4'
  },
  dropzoneFilled: {
    borderColor: '#10b981',
    borderStyle: 'solid',
    background: '#f0fdf4'
  },
  dropPrompt: {},
  dropIcon: { fontSize: '2.5rem', marginBottom: '10px' },
  dropText: { fontWeight: 600, color: '#334155', marginBottom: '4px' },
  dropOr: { color: '#94a3b8', fontSize: '0.85rem' },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'center'
  },
  fileIcon: { fontSize: '2rem' },
  fileName: { fontWeight: 600, color: '#1e293b', wordBreak: 'break-all' },
  fileSize: { color: '#64748b', fontSize: '0.82rem' },
  removeBtn: {
    background: '#fee2e2',
    border: 'none',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: '#ef4444',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '24px',
    padding: 0,
    flexShrink: 0
  },
  optionsRow: {
    display: 'flex',
    gap: '20px',
    marginBottom: '24px',
    flexWrap: 'wrap'
  },
  optionGroup: { flex: '1 1 200px' },
  label: {
    display: 'block',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
    fontSize: '0.9rem'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '2px solid #e2e8f0',
    fontSize: '0.9rem',
    color: '#1e293b',
    background: 'white',
    cursor: 'pointer'
  },
  toggle: {
    display: 'flex',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  toggleBtn: {
    flex: 1,
    padding: '10px',
    border: 'none',
    background: 'white',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#64748b',
    transition: 'all 0.15s'
  },
  toggleActive: {
    background: '#10b981',
    color: 'white'
  },
  progressWrap: { marginBottom: '20px' },
  progressTrack: {
    background: '#e2e8f0',
    borderRadius: '999px',
    height: '8px',
    overflow: 'hidden',
    marginBottom: '8px'
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #10b981, #3b82f6)',
    borderRadius: '999px',
    transition: 'width 0.4s ease'
  },
  progressLabel: { color: '#64748b', fontSize: '0.85rem', textAlign: 'center' },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '0.9rem'
  },
  success: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  successIcon: { fontSize: '1.4rem', flexShrink: 0 },
  successTitle: { fontWeight: 700, color: '#065f46', marginBottom: '4px' },
  successDetail: { color: '#047857', fontSize: '0.9rem' },
  actions: { display: 'flex', gap: '12px' },
  primaryBtn: {
    flex: 1,
    padding: '13px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  secondaryBtn: {
    padding: '13px 20px',
    background: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.95rem',
    cursor: 'pointer'
  },
  disabledBtn: {
    background: '#94a3b8',
    cursor: 'not-allowed'
  },
  authGate: {
    textAlign: 'center',
    padding: '32px 16px'
  },
  authGateIcon: { fontSize: '2.5rem', marginBottom: '12px' },
  authGateMsg: {
    color: '#374151',
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: '24px'
  },
  authGateBtns: { display: 'flex', gap: '12px', justifyContent: 'center' },
  signupBtn: {
    flex: 1,
    maxWidth: '160px',
    padding: '13px',
    background: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer'
  },
  limitBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  limitIcon: { fontSize: '1.5rem', flexShrink: 0 },
  limitTitle: { fontWeight: 700, color: '#92400e', marginBottom: '2px' },
  limitMsg: { color: '#b45309', fontSize: '0.9rem' },
  upgradeBtn: {
    marginLeft: 'auto',
    padding: '10px 18px',
    background: '#f59e0b',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
    flexShrink: 0
  },
  toast: {
    position: 'fixed',
    bottom: '32px',
    right: '24px',
    background: '#1e293b',
    color: 'white',
    borderRadius: '12px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    maxWidth: '360px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    zIndex: 9999,
    animation: 'slideUp 0.3s ease'
  },
  toastIcon: { fontSize: '1.4rem', flexShrink: 0 },
  toastTitle: { fontWeight: 700, fontSize: '0.95rem', marginBottom: '4px', color: '#f8fafc' },
  toastBody: { fontSize: '0.85rem', color: '#94a3b8', lineHeight: 1.4 },
  toastClose: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '1.2rem',
    cursor: 'pointer',
    padding: '0 0 0 8px',
    flexShrink: 0,
    lineHeight: 1
  },
  popupOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  popupCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '40px 36px',
    maxWidth: '480px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
  },
  popupIcon: { fontSize: '3rem', marginBottom: '16px' },
  popupTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 16px'
  },
  popupBody: {
    color: '#475569',
    fontSize: '1rem',
    lineHeight: 1.6,
    margin: '0 0 28px'
  },
  popupBtn: {
    background: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 32px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer'
  }
};

export default ConvertPage;
